import { normalizeCslType } from './csl-type-aliases';

/**
 * Turn a record from an external catalog (CrossRef, OpenAlex) into strict
 * CSL-JSON.
 *
 * WHY THIS LIVES IN `cite`. The reference server has done this since Slice 5,
 * via `@citation-js/core` — `new Cite(record).format('data')`. That works and is
 * staying there for now, but it is a Node dependency, and the same job is
 * needed in the BROWSER: catalog search moves client-side so its rate limit is
 * charged per user instead of per platform (Crossref allows 3 list requests a
 * second, per SOURCE, and a server-side proxy makes the whole platform one
 * source). Rather than write a second normaliser in the Angular package, the
 * knowledge lands here, beside `CSL_TYPE_ALIASES` — which already exists
 * precisely to map CrossRef's and OpenAlex's vocabularies onto CSL's, and is
 * already dated per row against those catalogs' own type lists.
 *
 * WHAT IT DOES, and does not:
 *   - maps `type` through {@link normalizeCslType}, so `journal-article`
 *     becomes `article-journal` and an unknown type becomes `document` rather
 *     than something the schemas cannot validate;
 *   - keeps only CSL variables, dropping the bookkeeping these catalogs ship
 *     alongside (`score`, `reference`, `is-referenced-by-count`, …), which the
 *     strict CSL-data schema the editor validates against rejects;
 *   - coerces the array-where-scalar fields both catalogs use (`title`,
 *     `container-title` and `ISSN` all arrive as arrays);
 *   - drops a CSL TEXT variable that did not arrive as text. `license` is the
 *     one that bites: it is a real CSL variable, so an allowlist keeps it, but
 *     CrossRef sends `license: [{ URL, content-version, delay-in-days, start }]`
 *     — an array of objects where CSL 1.0.2 types a string. Passing that through
 *     fails the same strict schema this function exists to satisfy;
 *   - strips per-name bookkeeping (`sequence`, `affiliation`, `role`) that is
 *     not part of a CSL name variable.
 *
 * It does NOT validate. A record that is missing a required field for its type
 * comes back missing it — the caller's schema layer is what decides whether
 * that is usable, and silently inventing a value would be worse than the gap.
 *
 * PRECONDITION, and it is the one an OpenAlex caller trips over. This expects a
 * record that already uses CSL-ish KEYS. OpenAlex's own `publication_date` and
 * `publication_year` are not CSL variables, so they are dropped like any other
 * catalog bookkeeping and the record comes back with no `issued` at all. A
 * caller reading OpenAlex must map those to `issued` before calling. Handling
 * them here would mean this function knowing which catalog it is looking at,
 * which is the thing `CSL_TYPE_ALIASES` already keeps out of the shape layer.
 */

/**
 * The CSL-JSON standard variables, from the CSL 1.0.2 data schema
 * (`resource.citationstyles.org/schema/v1.0/input/json/csl-data.json`,
 * checked 2026-09-10). Curated and dated the same way `CSL_TYPE_ALIASES` is,
 * because the per-type schemas in this folder are TypeScript interfaces — a
 * runtime allowlist cannot be derived from them.
 *
 * `id` and `type` are handled separately below and deliberately absent here.
 */
const CSL_VARIABLES: ReadonlySet<string> = new Set([
  // Names
  'author', 'chair', 'collection-editor', 'compiler', 'composer',
  'container-author', 'contributor', 'curator', 'director', 'editor',
  'editorial-director', 'executive-producer', 'guest', 'host', 'illustrator',
  'interviewer', 'narrator', 'organizer', 'original-author', 'performer',
  'producer', 'recipient', 'reviewed-author', 'script-writer', 'series-creator',
  'translator',
  // Dates
  'accessed', 'available-date', 'event-date', 'issued', 'original-date',
  'submitted',
  // Numbers
  'chapter-number', 'citation-number', 'collection-number', 'edition',
  'first-reference-note-number', 'issue', 'locator', 'number', 'number-of-pages',
  'number-of-volumes', 'page', 'page-first', 'part-number', 'printing-number',
  'section', 'supplement-number', 'version', 'volume',
  // Text
  'abstract', 'annote', 'archive', 'archive-collection', 'archive-location',
  'archive-place', 'authority', 'call-number', 'citation-key', 'citation-label',
  'collection-title', 'container-title', 'container-title-short', 'dimensions',
  'division', 'DOI', 'event', 'event-place', 'event-title', 'genre', 'ISBN',
  'ISSN', 'journalAbbreviation', 'jurisdiction', 'keyword', 'language',
  'license', 'medium', 'note', 'original-publisher', 'original-publisher-place',
  'original-title', 'part-title', 'PMCID', 'PMID', 'publisher',
  'publisher-place', 'references', 'reviewed-genre', 'reviewed-title', 'scale',
  'source', 'status', 'title', 'title-short', 'URL', 'volume-title',
  'volume-title-short', 'year-suffix',
  // Misc
  'categories', 'custom',
]);

/**
 * CSL variables that are scalars, but which CrossRef and OpenAlex commonly send
 * as single-element arrays. Anything not listed keeps whatever shape it came in
 * with — coercing blindly would flatten a legitimately repeatable field.
 */
const SCALAR_VARIABLES: ReadonlySet<string> = new Set([
  'title', 'container-title', 'container-title-short', 'collection-title',
  'ISSN', 'ISBN', 'publisher', 'publisher-place', 'event-place', 'archive',
  'abstract', 'language', 'DOI', 'URL', 'volume', 'issue', 'page', 'genre',
  'journalAbbreviation', 'original-title', 'title-short',
]);

/**
 * CSL variables that MUST be text, and which a catalog sends as something else.
 *
 * Distinct from {@link SCALAR_VARIABLES}: that one unwraps a single-element
 * array whose element is already the right thing. This one is for a value whose
 * shape is wrong however it is unwrapped, so the only honest outcome is to drop
 * it — a strict CSL consumer rejects the record otherwise, and inventing a
 * string out of an object would be worse than the gap.
 *
 * `license` is the case: CSL 1.0.2 types it as text, CrossRef sends an array of
 * objects (`{ URL, content-version, delay-in-days, start }`). A record that
 * really does carry `license: "CC-BY-4.0"` keeps it.
 */
const TEXT_ONLY_VARIABLES: ReadonlySet<string> = new Set(['license']);

/** The keys a CSL name variable may carry; everything else is the catalog's own bookkeeping. */
const CSL_NAME_KEYS: ReadonlySet<string> = new Set([
  'family', 'given', 'dropping-particle', 'non-dropping-particle', 'suffix',
  'comma-suffix', 'static-ordering', 'literal', 'parse-names',
]);

const NAME_VARIABLES: ReadonlySet<string> = new Set([
  'author', 'chair', 'collection-editor', 'compiler', 'composer',
  'container-author', 'contributor', 'curator', 'director', 'editor',
  'editorial-director', 'executive-producer', 'guest', 'host', 'illustrator',
  'interviewer', 'narrator', 'organizer', 'original-author', 'performer',
  'producer', 'recipient', 'reviewed-author', 'script-writer', 'series-creator',
  'translator',
]);

const isObject = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);

/** Drop the per-name bookkeeping CrossRef ships (`sequence`, `affiliation`, `role`). */
function cleanName(name: unknown): unknown {
  if (!isObject(name)) return name;
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(name)) {
    if (CSL_NAME_KEYS.has(key)) out[key] = name[key];
  }
  return out;
}

/**
 * First usable element of a single-element-array field, else the value itself.
 * An array with more than one entry keeps its first — both catalogs use the
 * array purely as a container, never to mean "several titles".
 */
function toScalar(value: unknown): unknown {
  if (!Array.isArray(value)) return value;
  return value.find((v) => v !== null && v !== undefined && v !== '') ?? undefined;
}

/**
 * Normalise one external catalog record to strict CSL-JSON.
 *
 * Pure, synchronous, and never throws: a record it cannot make sense of comes
 * back as the parts of it that ARE valid CSL, because a partial reference the
 * user can finish beats an exception they cannot.
 */
export function normalizeExternalRecord(record: unknown): Record<string, unknown> {
  if (!isObject(record)) return {};
  const out: Record<string, unknown> = {};

  // `type` first: everything downstream (the schema, the edit form, the
  // completion type-check) keys off it, and an absent one still resolves to the
  // generic `document` rather than being left undefined.
  const rawType = typeof record['type'] === 'string' ? (record['type'] as string) : '';
  out['type'] = normalizeCslType(rawType);

  if (typeof record['id'] === 'string' || typeof record['id'] === 'number') {
    out['id'] = record['id'];
  }

  for (const key of Object.keys(record)) {
    if (key === 'type' || key === 'id') continue;
    if (!CSL_VARIABLES.has(key)) continue;

    const value = record[key];
    if (value === null || value === undefined) continue;

    if (NAME_VARIABLES.has(key)) {
      if (Array.isArray(value)) {
        const names = value.map(cleanName).filter((n) => isObject(n) && Object.keys(n).length > 0);
        if (names.length) out[key] = names;
      }
      continue;
    }

    if (SCALAR_VARIABLES.has(key)) {
      const scalar = toScalar(value);
      if (scalar !== undefined && scalar !== '') out[key] = scalar;
      continue;
    }

    if (TEXT_ONLY_VARIABLES.has(key) && typeof value !== 'string') continue;

    out[key] = value;
  }

  return out;
}
